import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye } from 'lucide-react';
import { Field, InfoTip, inputCls, btnPrimary, btnGhost, grid3 } from '../components/shared/FormKit';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';
import QuizPreviewModal, { normalizePreviewQuestions } from '../components/lnd/QuizPreviewModal';
import QuizQuestionsEditor, {
  blankQuestion, fromServer, toPayload, paperProblem,
} from '../components/lnd/QuizQuestionsEditor';

/**
 * The trainer's quiz editor — /my/sessions/:id/quiz (docs/modules/ld.md §5.6).
 *
 * The same question cards as the Quiz Bank editor, aimed at ONE session: the
 * paper is created already attached to the session (title defaults to the
 * course title, category = the session's), and only the trainers of the
 * session and L&D get here. Everyone on the expected list sits it — there is
 * no audience to choose (docs/modules/ld.md §5.3).
 *
 * The server says whether the paper may be changed from here (`editable`,
 * `editBlock`): a paper other sessions share, one somebody has sat, or a
 * session marked completed is read-only — the page explains which.
 */

const EDIT_BLOCK_TEXT = {
  completed: 'This session is marked completed — the quiz is frozen. L&D can still change it from Training Records.',
  shared: 'This quiz is shared with other sessions. Go back to the session page and make a copy for this session to edit it, or ask L&D to edit it in the Quiz Bank.',
  locked: 'Someone has already sat this quiz — its questions are frozen so recorded scores keep their meaning. The title, pass mark, description and audience still save.',
};

const MySessionQuizEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ctx, setCtx] = useState(null);        // the quiz-editor envelope
  const [title, setTitle] = useState('');
  const [passMark, setPassMark] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState(() => [blankQuestion()]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [denied, setDenied] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/sessions/${id}/quiz-editor`);
      setCtx(data);
      const q = data.quiz;
      setTitle(q ? q.title : data.session.courseTitle);
      setPassMark(q && q.passMark != null ? String(q.passMark) : '');
      setDescription(q ? (q.description || '') : '');
      setQuestions(q && (q.questions || []).length > 0 ? q.questions.map(fromServer) : [blankQuestion()]);
    } catch (e) {
      if (e.status === 403) setDenied(true);
      else setError(e.message);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const backTo = `/my/sessions/${id}`;
  const back = (
    <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
      <ArrowLeft className="w-4 h-4" /> Session
    </Link>
  );

  if (denied) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        {back}
        <div className="mt-4 bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-10 text-center">
          <p className="text-sm text-slate-600">Only the session’s trainers and L&D can open its quiz.</p>
        </div>
      </div>
    );
  }

  const quiz = ctx?.quiz || null;
  const isNew = !quiz;
  const editBlock = ctx?.editBlock || null;
  // The header (title, pass mark, description, audience) saves whenever the
  // paper is this session's own and the session is open — even once the
  // paper is locked; the questions freeze with the first sitting.
  const readOnly = !ctx || (isNew
    ? !ctx.canCreate
    : !(ctx.canEdit && ctx.ownership?.exclusive && !ctx.session.completed));
  const questionsLocked = Boolean(quiz && quiz.locked);

  const save = async () => {
    setError(null);
    setSaved(false);
    if (!title.trim()) { setError('Give the quiz a title.'); return; }
    if (passMark === '' || !Number.isInteger(Number(passMark)) || Number(passMark) < 1 || Number(passMark) > 100) {
      setError('Pass mark is required — a whole number between 1 and 100.');
      return;
    }
    if (!questionsLocked) {
      const problem = paperProblem(questions);
      if (problem) { setError(problem); return; }
    }
    const header = {
      title: title.trim(),
      passMark: Number(passMark),
      description: description.trim() || null,
    };
    setSaving(true);
    try {
      if (isNew) {
        await fetchJSON(`/api/ld/sessions/${id}/quiz`, {
          method: 'POST', body: JSON.stringify({ ...header, questions: toPayload(questions) }),
        });
        navigate(backTo, { replace: true });
      } else {
        await fetchJSON(`/api/ld/sessions/${id}/quiz`, {
          method: 'PUT',
          body: JSON.stringify(questionsLocked ? header : { ...header, questions: toPayload(questions) }),
        });
        setSaved(true);
        load();
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const openPreview = () => {
    setError(null);
    const problem = paperProblem(questions);
    if (problem) { setError(`Cannot preview yet — ${problem}`); return; }
    setPreview({
      title: title.trim() || 'Untitled quiz',
      description: description.trim() || null,
      passMark: Number(passMark),
      questions: normalizePreviewQuestions(questions),
    });
  };

  const totalPoints = questions.reduce((s, q) => s + (Number(q.points) || 0), 0);
  const session = ctx?.session;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {back}
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{isNew ? 'Create Quiz' : readOnly ? 'Quiz' : 'Edit Quiz'}</h1>
          {session && (
            <p className="text-sm text-slate-500 mt-1 truncate">
              {session.courseTitle} · {formatDate(session.date)}{session.categoryTitle ? ` · ${session.categoryTitle}` : ''}
            </p>
          )}
          <p className="text-xs text-slate-500 mt-0.5">
            {questions.length} question{questions.length === 1 ? '' : 's'} · {totalPoints} point{totalPoints === 1 ? '' : 's'} in total
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={openPreview}
            disabled={loading}
            title="Sit the quiz yourself, exactly as a participant would — nothing is recorded"
            className={`${btnGhost} inline-flex items-center gap-1.5 border border-slate-300 disabled:opacity-50`}
          >
            <Eye className="w-4 h-4" /> Preview
          </button>
          {!readOnly && (
            <button type="button" onClick={save} disabled={saving || loading} className={btnPrimary}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      {editBlock && (
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
          {EDIT_BLOCK_TEXT[editBlock]}
          {editBlock === 'shared' && ctx?.copyable && (
            <> <Link to={backTo} className="text-blue-600 hover:text-blue-800 font-medium">Back to the session page</Link></>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      {saved && !error && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
          Saved.
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6 space-y-4">
            <Field label="Title" required>
              <input
                type="text"
                value={title}
                disabled={readOnly}
                onChange={(e) => setTitle(e.target.value)}
                className={`${inputCls} disabled:bg-slate-50`}
                maxLength={255}
              />
            </Field>
            <div className={grid3}>
              <Field
                label={<>Pass Mark (%) <InfoTip text="A sitting scoring at or above this percentage is marked Passed; below it the participant retakes." /></>}
                required
              >
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={passMark}
                  disabled={readOnly}
                  onChange={(e) => setPassMark(e.target.value)}
                  className={`${inputCls} disabled:bg-slate-50`}
                  placeholder="e.g. 70"
                />
              </Field>
            </div>
            <p className="text-xs text-slate-500 sm:-mt-2">Everyone on the attendee list sits the quiz, whether or not they attended — passing it earns the session’s hours.</p>
            <Field label="Description" hint="Shown to the participant on the quiz's start page.">
              <textarea
                rows="2"
                value={description}
                disabled={readOnly}
                onChange={(e) => setDescription(e.target.value)}
                className={`${inputCls} disabled:bg-slate-50`}
                placeholder="What the quiz covers, or any instructions."
                maxLength={5000}
              />
            </Field>
            {session?.categoryTitle && (
              <p className="text-xs text-slate-500">Category: {session.categoryTitle} (the session’s category — set by L&D).</p>
            )}
          </div>

          <QuizQuestionsEditor questions={questions} setQuestions={setQuestions} locked={readOnly || questionsLocked} />

          <div className="flex justify-end gap-2 pt-2">
            <Link to={backTo} className={btnGhost}>Back to Session</Link>
            <button type="button" onClick={openPreview} className={`${btnGhost} inline-flex items-center gap-1.5 border border-slate-300`}>
              <Eye className="w-4 h-4" /> Preview
            </button>
            {!readOnly && (
              <button type="button" onClick={save} disabled={saving} className={btnPrimary}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      )}

      {preview && (
        <QuizPreviewModal
          title={preview.title}
          description={preview.description}
          passMark={preview.passMark}
          questions={preview.questions}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

export default MySessionQuizEditor;
